import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { ChatSliceState, ChatMessage, RootState, WorkflowStatus, GenerationStatus } from "./types/redux";
import {
  getDirectLine,
  getConversationMetadata,
  ensureDirectLineOnline,
  attachIdentityToActivity,
  isDirectLineHealthy,
  isDirectLineTerminal,
} from "./directLlineClient";
import { ensureDirectLineHealthy, notifyHealthAck } from "./directLineHealth";
import { acquireProjectSwitchLock } from "./projectSwitchLock";
// Phase 1: Import token service for acquiring Graph tokens in Redux thunks
import {
  getCurrentMsalAccount,
  acquireDelegatedGraphToken,
} from "./utils/msalTokenService";
// Import the new actions from resultsSlice
import {
  fetchResultsPending,
  setResults,
  fetchResultsRejected,
  clearResults,
  appendFact,
  revertLoadingToSucceeded,
} from "./ResultsSlice_Store";
// Import project slice actions
import {
  setProjectId,
  markInitialized,
  updateProjectMetadata,
  setCurrentProject,
  upsertUserProject,
  selectCurrentProjectType,
} from "./ProjectSlice_Store";
// Import layout slice actions
import { setLayoutMode } from "./LayoutSlice_Store";
// Import feature flag resolver (non-React)
import { getFeatureFlag } from "./hooks/useFeatureFlag";
// Import document preview slice actions
import { closeAllDocumentPreviews, invalidateDocumentPreview, refreshDocumentByName } from "./DocumentPreviewSlice_Store";
// Import draft selection slice actions
import { clearProjectSelections } from "./DraftSelectionSlice_Store";
// Import message type constants
import {
  MESSAGE_TYPE_CHAT_RESPONSE,
  MESSAGE_TYPE_CHAT_RESPONSE_CHUNK,
  MESSAGE_TYPE_RESULTS_PAYLOAD,
  MESSAGE_TYPE_RESULTS_PAYLOAD_CHUNK,
  MESSAGE_TYPE_FACTS_STREAM_ITEM,
  MESSAGE_TYPE_BOOTSTRAP_ACK,
  MESSAGE_TYPE_BOOTSTRAP_ERROR,
  MESSAGE_TYPE_WORKFLOW_STATUS,
} from "./constants/messageTypes";
// Import control event constants
import {
  CONTROL_EVENT_HEALTH_ACK,
  CONTROL_EVENT_BOOTSTRAP,
  CONTROL_EVENT_HEALTH_PROBE,
} from "./constants/chatControlEvents";
// Phase 6: Import new streaming slice actions
import {
  messageChunkReceived as newMessageChunkReceived,
  finalizeMessage as newFinalizeMessage
} from "./streaming/streamingSlice";
import { convertRawTablesToPlaceholders } from "./helpers/inlineFacts";
import { THRESHOLD_DEFAULTS, ALLOWED_THRESHOLD_KEYS } from "./constants/thresholdDefaults";

interface UpdateMessagePayload {
  clientMessageId?: string;
  text?: string;
  updates?: Record<string, unknown>;
}


interface StageTurnResultsPayload {
  turnId: string;
  results?: unknown[];
  documents?: unknown[];
}

/**
 * Migrate old fact format to new format for backward compatibility
 * Old format: ^fid=xxx^ (with fid= prefix)
 * New format: [[FACT:xxx]]
 *
 * This ensures messages stored in the backend with old format are transparently
 * converted when loaded, without requiring database migration.
 */
const migrateFactFormat = (text: string): string => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // Pattern: Match ^fid=xxx^ format (with fid= prefix)
  const migratedText = text.replace(/\^fid=([^^]+)\^/g, '[[FACT:$1]]');

  return migratedText;
};

/**
 * Apply all text migrations to a rehydrated message.
 * Centralises the transformation chain so every rehydration point stays consistent.
 */
const migrateMessageContent = (text: string): string =>
  convertRawTablesToPlaceholders(migrateFactFormat(text));

interface SetTurnTodoPlanPayload {
  turnId: string;
  plan?: unknown;
  scopeSummary?: unknown;
}

interface ClearTurnTodoPlanPayload {
  turnId: string;
}

interface AddGenerationProgressPayload {
  projectId: string;
}

interface UpdateGenerationStatusPayload {
  projectId: string;
  status: GenerationStatus;
  error?: string;
}

// Chunk accumulator for reassembling chunked results payloads
interface ChunkAccumulator {
  chunks: Array<{
    chunkIndex: number;
    payload: {
      results: unknown[];
      documents: unknown[];
      facts: unknown[];
    };
    sizeBytes: number;
  }>;
  expected: number;
  receivedBytes: number;
  turnId: string;
  projectId: string;
}

const initialState: ChatSliceState = {
  messages: [
    {
      sender: "chatbot",
      text: "Welcome! I'm here to help you with deal analysis and research. Please choose one of the options below to get started.",
    },
  ],
  isBotTyping: false,
  mcpEnvironment: "primary", // 'primary' or 'secondary'
  dataVersion: 2, // 1 for 'Synthetic', 2 for 'Public'
  conversationId: null, // DirectLine conversation ID for debugging and testing
  bootstrapInitialized: false, // Track if bootstrap has been attempted
  bootstrapError: null, // Track bootstrap errors
  pendingStreamingMessages: {}, // Map of turnId -> { text, sequence, messageIndex }
  currentWorkflowStatus: null, // Current workflow status for action "thinking" indicators { label, action, visible }
  chatInputReady: true, // Flag to control whether user can submit new messages
  useCaseSelected: false, // Track if user has selected a use case to unlock chat
  selectedUseCase: null as string | null, // Track which use case was selected: 'past-deals', 'offer-letter', etc.
  pendingTurnResults: {}, // Phase 3: Map of turnId -> { results, documents } staged for finalization
  todoPlanByTurn: {}, // Map of turnId -> { plan: TurnTodoPlan, scopeSummary?: object }
  chatHistoryLoading: false, // Track if we're loading conversation history
  chatHistoryError: null, // Track errors when loading conversation history
};

// Use a Set to prevent processing the same activity ID multiple times
const handledActivityIds = new Set<string>();

// Generate a unique session ID on module load to detect page refreshes
// This is more reliable than time-based detection
const currentSessionId = Math.random().toString(36).substring(7);
let lastSessionId: string | null = null;

// Track subscriptions and listener state for proper cleanup
let isListenerInitialized = false;
let activitySubscription: { unsubscribe: () => void } | null = null;

// Track highest status sequence per turn for out-of-order handling (fire-and-forget can cause late arrivals)
const highestStatusSequenceByTurn: Map<string, number> = new Map();

// Mutex for DirectLine reconnection — prevents concurrent reconnection attempts
let reconnectionInProgress: Promise<void> | null = null;

// AbortController for cancelling ongoing conversation loads (rehydration, project fetch, etc.)
// This allows new chat/project selection to cancel any in-flight requests
let conversationLoadAbortController: AbortController | null = null;

/**
 * Cancel any ongoing conversation load operations.
 * Call this before starting a new project or switching conversations.
 */
export const cancelOngoingConversationLoad = () => {
  if (conversationLoadAbortController) {
    console.log('🛑 [CANCEL] Aborting ongoing conversation load');
    conversationLoadAbortController.abort();
    conversationLoadAbortController = null;
  }
};

/**
 * Create a new AbortController for conversation loading.
 * Automatically cancels any existing controller first.
 */
const createConversationLoadAbortController = (): AbortController => {
  // Cancel any existing load first
  cancelOngoingConversationLoad();
  // Create and return new controller
  conversationLoadAbortController = new AbortController();
  return conversationLoadAbortController;
};

// Phase 3: Track chunk accumulators for reassembling chunked results payloads
// Map of chunkId -> ChunkAccumulator
const chunkAccumulatorsByChunkId = new Map<string, ChunkAccumulator>();

const chatSlice = createSlice({
  name: "chatSlice",
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },
    updateMessageByClientId: (state, action: PayloadAction<UpdateMessagePayload>) => {
      const { clientMessageId, text, updates } = action.payload || {};
      if (!clientMessageId) return;
      const target = state.messages.find(
        (message) => message.clientMessageId === clientMessageId
      );
      if (!target) return;

      if (updates && typeof updates === "object") {
        Object.assign(target, updates);
      }

      if (text !== undefined) {
        target.text = text;
      }
    },
    removeMessageByClientId: (state, action: PayloadAction<string>) => {
      const clientMessageId = action.payload;
      if (!clientMessageId) return;
      state.messages = state.messages.filter(
        (message) => message.clientMessageId !== clientMessageId
      );
    },
    updateMessageText: (state, action: PayloadAction<{ index: number; text?: string; suggested_actions?: unknown[] }>) => {
      const { index, text, suggested_actions } = action.payload;
      if (index >= 0 && index < state.messages.length) {
        if (text !== undefined) {
          state.messages[index].text = text;
        }
        if (suggested_actions && Array.isArray(suggested_actions)) {
          state.messages[index].suggested_actions = suggested_actions as import("./types/redux").SuggestedAction[];
        }
      }
    },
    updateUploadInTable: (state, action: PayloadAction<Partial<import('./types/redux').UploadItem> & { uploadId: string }>) => {
      const { uploadId, fileName, fileSize, progress, status, completed, error, docId } = action.payload;
      if (!uploadId) return;

      // Find existing upload-table message that's not yet completed, or create new one
      let uploadTableMessage = state.messages.find(
        (msg: ChatMessage) => msg.type === "upload-table" && !msg.allCompleted
      ) as ChatMessage | undefined;

      if (!uploadTableMessage) {
        // Create new upload-table message
        uploadTableMessage = {
          sender: "chatbot",
          type: "upload-table",
          clientMessageId: `upload-table-${Date.now()}`,
          text: "",
          uploads: {},
          allCompleted: false,
        };
        state.messages.push(uploadTableMessage);
      }

      // Update or add the specific upload (merge with existing if present)
      const existingUpload: Partial<import('./types/redux').UploadItem> = (uploadTableMessage.uploads as Record<string, import('./types/redux').UploadItem>)?.[uploadId] || {};
      if (!uploadTableMessage.uploads) {
        uploadTableMessage.uploads = {};
      }
      (uploadTableMessage.uploads as Record<string, import('./types/redux').UploadItem>)[uploadId] = {
        id: uploadId,
        fileName: fileName !== undefined ? fileName : existingUpload.fileName,
        fileSize: fileSize !== undefined ? fileSize : existingUpload.fileSize,
        progress: progress !== undefined ? progress : (existingUpload.progress || 0),
        status: status !== undefined ? status : existingUpload.status,
        completed: completed !== undefined ? completed : (existingUpload.completed || false),
        error: error !== undefined ? error : (existingUpload.error || false),
        docId: docId !== undefined ? docId : existingUpload.docId,
      };

      // Check if all uploads are completed (blob upload done OR error)
      const allUploads = Object.values(uploadTableMessage.uploads as Record<string, import('./types/redux').UploadItem>);
      const allDone = allUploads.every((upload: import('./types/redux').UploadItem) => upload.completed || upload.error);

      if (allDone && allUploads.length > 0 && !uploadTableMessage.allCompleted) {
        uploadTableMessage.allCompleted = true;

        // Count successful vs failed uploads
        const successCount = allUploads.filter((u: import('./types/redux').UploadItem) => u.completed && !u.error).length;
        const failCount = allUploads.filter((u: import('./types/redux').UploadItem) => u.error).length;

        // Check if all successful uploads have been fully processed (status: "Ready")
        const allProcessed = allUploads
          .filter((u: import('./types/redux').UploadItem) => u.completed && !u.error)
          .every((u: import('./types/redux').UploadItem) => u.status === "Ready");

        // Build appropriate message based on processing state
        let summaryText: string;
        if (failCount === 0 && allProcessed) {
          // All files uploaded AND processed - prompt user to start
          summaryText = successCount === 1
            ? "Your file is ready. Do you have more files to upload, or can I proceed to read it, extract relevant context, and create an outline for you to review?"
            : `All ${successCount} files are ready. Do you have more files to upload, or can I proceed to read them, extract relevant context, and create an outline for you to review?`;
        } else if (failCount === 0) {
          // Files uploaded but still processing
          summaryText = successCount === 1
            ? "1 file uploaded successfully."
            : `${successCount} files uploaded successfully.`;
        } else if (successCount === 0) {
          const rawErrorMsg = failCount === 1 && allUploads[0]?.status && allUploads[0].status !== 'error'
            ? allUploads[0].status.replace(/[\r\n]+/g, ' ').trim()
            : null;
          const singleErrorMsg = rawErrorMsg && rawErrorMsg.length > 200
            ? rawErrorMsg.slice(0, 200) + '…'
            : rawErrorMsg;
          summaryText = failCount === 1
            ? (singleErrorMsg ? `1 file failed to upload: ${singleErrorMsg}` : "1 file failed to upload.")
            : `${failCount} files failed to upload.`;
        } else {
          summaryText = `${successCount} file${successCount !== 1 ? 's' : ''} uploaded, ${failCount} failed.`;
        }

        state.messages.push({
          sender: "chatbot",
          text: summaryText,
          type: allProcessed && failCount === 0 ? "upload-ready-prompt" : "upload-summary",
        });
      } else if (uploadTableMessage.allCompleted) {
        // Upload table was already marked complete, but we got another status update
        // Check if all uploads are now "Ready" and we haven't shown the prompt yet
        const allProcessed = allUploads
          .filter((u: import('./types/redux').UploadItem) => u.completed && !u.error)
          .every((u: import('./types/redux').UploadItem) => u.status === "Ready");

        const hasReadyPrompt = state.messages.some(
          (msg: ChatMessage) => msg.type === "upload-ready-prompt"
        );

        if (allProcessed && !hasReadyPrompt) {
          const successCount = allUploads.filter((u: import('./types/redux').UploadItem) => u.completed && !u.error).length;
          const summaryText = successCount === 1
            ? "Your file is ready. Do you have more files to upload, or can I proceed to read it, extract relevant context, and create an outline for you to review?"
            : `All ${successCount} files are ready. Do you have more files to upload, or can I proceed to read them, extract relevant context, and create an outline for you to review?`;

          state.messages.push({
            sender: "chatbot",
            text: summaryText,
            type: "upload-ready-prompt",
          });
        }
      }
    },
    clearUploadsTable: (state) => {
      state.messages = state.messages.filter((msg: ChatMessage) => msg.type !== "upload-table");
    },
    addGenerationProgress: (state, action: PayloadAction<AddGenerationProgressPayload>) => {
      const { projectId } = action.payload;
      // Check if a generation-progress message already exists for this project
      const existingProgressIndex = state.messages.findIndex(
        (msg: ChatMessage) => msg.type === "generation-progress" && msg.projectId === projectId
      );
      if (existingProgressIndex !== -1) {
        const existingProgress = state.messages[existingProgressIndex];
        // If it's still generating, skip duplicate
        if (existingProgress.status === "generating") {
          console.log(`⚠️ Generation already in progress for ${projectId}, skipping duplicate`);
          return;
        }
        // If it's completed/failed, remove the old one to make way for the new regeneration
        console.log(`🔄 Removing completed progress bar for ${projectId} to start fresh regeneration`);
        state.messages.splice(existingProgressIndex, 1);
      }
      // Add a generation progress message to the chat
      state.messages.push({
        sender: "system",
        type: "generation-progress",
        projectId,
        startTime: Date.now(),
        status: "generating",
        text: "Generating draft content...",
      } as ChatMessage);
    },
    updateGenerationStatus: (state, action: PayloadAction<UpdateGenerationStatusPayload>) => {
      const { projectId, status, error } = action.payload;
      // Find the generation-progress message for this project and update it
      const progressMessage = state.messages.find(
        (msg: ChatMessage) => msg.type === "generation-progress" && msg.projectId === projectId
      );
      if (progressMessage) {
        progressMessage.status = status;
        if (error) {
          progressMessage.error = error;
        }
      }
    },
    setBotTyping: (state, action: PayloadAction<boolean>) => {
      state.isBotTyping = action.payload;
    },
    setMcpEnvironment: (state, action: PayloadAction<string>) => {
      state.mcpEnvironment = action.payload;
    },
    setDataVersion: (state, action: PayloadAction<number>) => {
      state.dataVersion = action.payload;
    },
    setConversationId: (state, action: PayloadAction<string | null>) => {
      state.conversationId = action.payload;
    },
    setBootstrapInitialized: (state, action: PayloadAction<boolean>) => {
      state.bootstrapInitialized = action.payload;
    },
    setBootstrapError: (state, action: PayloadAction<string | null>) => {
      state.bootstrapError = action.payload;
    },
    removeMessageByType: (state, action: PayloadAction<string>) => {
      state.messages = state.messages.filter(
        (msg: ChatMessage) => msg.type !== action.payload
      );
    },
    resetChatForNewProject: (state) => {
      // Reset chat state for a new project while preserving welcome message
      state.messages = [
        {
          sender: "chatbot",
          text: "Welcome! I'm here to help you with deal analysis and research. Please choose one of the options below to get started.",
        },
      ];
      state.isBotTyping = false;
      state.bootstrapInitialized = false;
      state.bootstrapError = null;
      state.conversationId = null;
      state.pendingStreamingMessages = {};
      state.currentWorkflowStatus = null;
      state.chatInputReady = true; // Unlock input for fresh projects
      state.useCaseSelected = false; // Reset use case selection for new project
      state.selectedUseCase = null; // Reset specific use case for new project
      state.pendingTurnResults = {}; // Phase 3: Clear staged results
      state.todoPlanByTurn = {}; // Clear todo plans
      // Note: mcpEnvironment and dataVersion are preserved
    },
    setWorkflowStatus: (state, action: PayloadAction<WorkflowStatus | null>) => {
      // Update workflow status for action "thinking" indicators
      state.currentWorkflowStatus = action.payload;
    },
    setChatInputReady: (state, action: PayloadAction<boolean>) => {
      state.chatInputReady = action.payload;
    },
    setUseCaseSelected: (state, action: PayloadAction<boolean>) => {
      state.useCaseSelected = action.payload;
    },
    setSelectedUseCase: (state, action: PayloadAction<string | null>) => {
      state.selectedUseCase = action.payload;
      // Also mark use case as selected when a specific use case is set
      if (action.payload) {
        state.useCaseSelected = true;
      }
    },
    stageTurnResults: (state, action: PayloadAction<StageTurnResultsPayload>) => {
      // Phase 3: Stage results payload for a turn to be merged when chat_response finalizes
      const { turnId, results, documents } = action.payload;
      if (turnId && state.pendingTurnResults) {
        state.pendingTurnResults[turnId] = { results, documents };
      }
    },
    setTurnTodoPlan: (state, action: PayloadAction<SetTurnTodoPlanPayload>) => {
      // Store todo plan for a turn, keyed by turnId
      const { turnId, plan, scopeSummary } = action.payload;
      console.log('📝 setTurnTodoPlan reducer called:', { turnId, plan, scopeSummary });
      if (turnId && plan && state.todoPlanByTurn) {
        state.todoPlanByTurn[turnId] = { plan, scopeSummary };
        console.log('✅ todoPlanByTurn updated:', state.todoPlanByTurn);
      } else {
        console.log('⚠️ setTurnTodoPlan: missing turnId or plan');
      }
    },
    clearTurnTodoPlan: (state, action: PayloadAction<ClearTurnTodoPlanPayload>) => {
      // Clear todo plan for a specific turn
      const { turnId } = action.payload;
      if (turnId && state.todoPlanByTurn && state.todoPlanByTurn[turnId]) {
        delete state.todoPlanByTurn[turnId];
      }
    },
    setChatHistoryLoading: (state, action: PayloadAction<boolean>) => {
      state.chatHistoryLoading = action.payload;
    },
    setChatHistoryError: (state, action: PayloadAction<string | null>) => {
      state.chatHistoryError = action.payload;
    },
    setMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      // Replace all messages with the provided array
      state.messages = action.payload;
    },
  },
});

export const {
  addMessage,
  setBotTyping,
  setMcpEnvironment,
  setDataVersion,
  setConversationId,
  setBootstrapInitialized,
  setBootstrapError,
  resetChatForNewProject,
  setWorkflowStatus,
  setChatInputReady,
  setUseCaseSelected,
  setSelectedUseCase,
  stageTurnResults,
  setTurnTodoPlan,
  clearTurnTodoPlan,
  addGenerationProgress,
  updateGenerationStatus,
  setChatHistoryLoading,
  setChatHistoryError,
  setMessages,
  removeMessageByType,
} = chatSlice.actions;
export const { updateMessageByClientId, removeMessageByClientId, updateMessageText, updateUploadInTable, clearUploadsTable } =
  chatSlice.actions;

// Helper action creators for chat input lock
export const lockChatInput = () => setChatInputReady(false);
export const unlockChatInput = () => setChatInputReady(true);

// --- Module State Reset ---
/**
 * Resets module-level state for a new project.
 * This includes the listener initialization flag, activity IDs set, and DirectLine connection.
 * Call this when starting a completely new project to ensure clean state.
 */
export const resetChatModuleState = async () => {
  console.log('🧹 [RESET] resetChatModuleState called');

  // Unsubscribe from old activity subscription to prevent "conversation ended" errors
  if (activitySubscription) {
    console.log('🧹 [RESET] Unsubscribing from activity subscription');
    activitySubscription.unsubscribe();
    activitySubscription = null;
  }

  // Reset listener initialization flag
  console.log('🧹 [RESET] Resetting listener initialization flag (was:', isListenerInitialized, ')');
  isListenerInitialized = false;

  // Clear handled activity IDs
  console.log('🧹 [RESET] Clearing handled activity IDs (count:', handledActivityIds.size, ')');
  handledActivityIds.clear();

  // Phase 3: Clear all chunk accumulators
  console.log('🧹 [RESET] Clearing chunk accumulators (count:', chunkAccumulatorsByChunkId.size, ')');
  chunkAccumulatorsByChunkId.clear();

  // Clear status sequence tracking for out-of-order handling
  console.log('🧹 [RESET] Clearing status sequence tracking (count:', highestStatusSequenceByTurn.size, ')');
  highestStatusSequenceByTurn.clear();

  // Reset DirectLine connection - must await to ensure old connection fully closes
  console.log('🧹 [RESET] Resetting DirectLine connection');
  const { resetDirectLine } = await import("./directLlineClient");
  await resetDirectLine();
  console.log('✅ [RESET] resetChatModuleState completed');
};

// --- Reconnection ---

/**
 * Transparently reconnect DirectLine when the connection has died.
 *
 * Performs the full reconnection cycle:
 * 1. Reset module-level state (listener, subscription, DirectLine instance)
 * 2. Reinitialize chat listener (new token, new connection, new subscription, health probe, bootstrap)
 *
 * Uses a mutex to prevent concurrent reconnections.
 * NOTE: Creates a NEW conversation — bot won't have previous message history,
 * but bootstrap re-sends project context.
 */
const reconnectDirectLine = () => async (dispatch: any, getState: any) => {
  // Mutex: if reconnection already in progress, piggyback on that promise
  if (reconnectionInProgress) {
    await reconnectionInProgress;
    return;
  }

  let resolveReconnection!: () => void;
  let rejectReconnection!: (err: Error) => void;

  reconnectionInProgress = new Promise<void>((resolve, reject) => {
    resolveReconnection = resolve;
    rejectReconnection = reject;
  });

  try {
    // Show reconnection feedback to user
    dispatch(addMessage({
      sender: "system",
      text: "Reconnecting to chat service...",
      type: "reconnection-status",
    }));

    await resetChatModuleState();
    dispatch(setBootstrapInitialized(false));
    dispatch(setBootstrapError(null));
    await dispatch(initializeChatListener());

    if (!isDirectLineHealthy()) {
      throw new Error('DirectLine reconnection completed but connection is not healthy');
    }

    // Replace status message with brief success message
    dispatch(removeMessageByType("reconnection-status"));
    const successMessageId = `reconnection-success-${Date.now()}`;
    dispatch(addMessage({
      sender: "system",
      text: "Reconnected successfully. Your message is being sent.",
      type: "reconnection-success",
      clientMessageId: successMessageId,
    }));

    // Auto-remove this specific success message after 3 seconds
    setTimeout(() => {
      dispatch(removeMessageByClientId(successMessageId));
    }, 3000);

    resolveReconnection();
  } catch (err) {
    console.error('❌ [RECONNECT] Reconnection failed:', err);

    dispatch(removeMessageByType("reconnection-status"));
    dispatch(addMessage({
      sender: "system",
      text: "Failed to reconnect to chat service. Please refresh the page.",
      type: "reconnection-error",
    }));

    rejectReconnection(err as Error);
    throw err;
  } finally {
    reconnectionInProgress = null;
  }
};

// --- Thunks ---

export const sendMessage = (text: string) => async (dispatch: any, getState: any) => {
  // Check if this is a control message
  const isControlMessage = text.startsWith("CONTROL_");

  const state = getState();
  const currentProjectId = state.projectSlice?.currentProjectId;

  if (!currentProjectId && !isControlMessage) {
    return;
  }

  // Short-circuit duplicate submits for non-control messages
  if (!isControlMessage && !state.chatSlice.chatInputReady) {
    return;
  }

  // Only add to chat history if it's not a control message
  if (!isControlMessage) {
    // Lock input immediately for non-control messages
    dispatch(lockChatInput());
    dispatch(addMessage({ sender: "user", text }));
    dispatch(setBotTyping(true));
    // Dispatch pending action to show loading state in the results panel
    dispatch(fetchResultsPending());
  }

  // Re-pull state in case dispatches above updated anything.
  const { mcpEnvironment, dataVersion, selectedUseCase } = getState().chatSlice;
  // Get project type from loaded project (fallback for reloaded projects)
  const projectType = selectCurrentProjectType(getState());
  // Use selectedUseCase (new project) or projectType (reloaded project)
  const effectiveUseCase = selectedUseCase || projectType;
  // Get username from auth state for auditing
  const { userName, userEmail } = getState().userAuthAndAdminSlice;
  // Get current project ID from project state
  const { currentProjectId: refreshedProjectId } = getState().projectSlice;

  const projectIdForChannelData = refreshedProjectId || currentProjectId;

  if (!projectIdForChannelData && !isControlMessage) {
    return;
  }

  // Get selected paragraph IDs from draft selection state
  const draftSelections = getState().draftSelectionSlice?.selections?.[projectIdForChannelData] || {};
  // Flatten all selected paragraph IDs across all documents into a single array
  const selectedParagraphIds = Object.values(draftSelections).flat();

  // Debug logging for paragraph selection
  console.log(`📝 [sendMessage] projectId=${projectIdForChannelData}, draftSelections=`, draftSelections, `selectedParagraphIds=`, selectedParagraphIds);

  try {
    // Pre-send health check: handle dead or still-connecting DirectLine
    if (!isDirectLineHealthy()) {
      if (isDirectLineTerminal()) {
        // Terminal state (Ended/ExpiredToken/FailedToConnect) — full reconnect required
        try {
          await dispatch(reconnectDirectLine());
        } catch (reconnectErr) {
          if (!isControlMessage) {
            dispatch(setBotTyping(false));
            dispatch(fetchResultsRejected("Connection lost. Please refresh the page."));
            dispatch(unlockChatInput());
          }
          return;
        }
      } else {
        // Transient state (Connecting/Uninitialized) — wait for it to come online
        try {
          await ensureDirectLineOnline();
        } catch (waitErr) {
          if (!isControlMessage) {
            dispatch(setBotTyping(false));
            dispatch(fetchResultsRejected("Connection lost. Please refresh the page."));
            dispatch(unlockChatInput());
          }
          return;
        }
      }
    }

    // Phase 1: Get MSAL account for DirectLine initialization
    const msalAccount = getCurrentMsalAccount();
    const directLine = await getDirectLine(null, msalAccount);

    const channelData: Record<string, any> = {
      mcp_environment: mcpEnvironment,
      data_version: dataVersion,
      message_type: isControlMessage ? "control" : "user",
      username: userName || userEmail,
      project_id: projectIdForChannelData,
      use_case: effectiveUseCase,  // Use selectedUseCase (new) or projectType (reloaded)
      selected_paragraph_ids: selectedParagraphIds,  // Include selected paragraphs for editing
    };

    // Inject threshold overrides if enabled in admin settings
    try {
      const overridesEnabled = localStorage.getItem('dealos.thresholdOverridesEnabled') === 'true';
      if (overridesEnabled) {
        const storedOverrides = localStorage.getItem('dealos.thresholdOverrides');
        if (storedOverrides) {
          const overrides = JSON.parse(storedOverrides);
          if (overrides && typeof overrides === 'object') {
            const nonDefaultOverrides: Record<string, number> = {};
            for (const key of ALLOWED_THRESHOLD_KEYS) {
              if (!(key in overrides)) continue;
              const numericValue = typeof overrides[key] === 'number' ? overrides[key] : Number(overrides[key]);
              if (!Number.isFinite(numericValue)) continue;
              if (key === 'page_size' && !Number.isInteger(numericValue)) continue;
              if (numericValue !== THRESHOLD_DEFAULTS[key]) {
                nonDefaultOverrides[key] = numericValue;
              }
            }
            if (Object.keys(nonDefaultOverrides).length > 0) {
              channelData.threshold_overrides = nonDefaultOverrides;
            }
          }
        }
      }
    } catch {
      // Ignore localStorage read/parse errors
    }

    // Phase 1: Acquire fresh delegated Graph token and attach identity
    let activity: any = {
      from: { role: "user" },
      type: "message",
      text,
      channelData,
    };

    // Ensure passport is valid (refresh if expired) before attaching identity
    try {
      const { ensureValidPassport } = await import("./directLlineClient");
      await ensureValidPassport();
    } catch (passportError) {
      console.error("❌ Failed to ensure valid passport — cannot send without valid auth:", passportError);
      if (!isControlMessage) {
        dispatch(setBotTyping(false));
        dispatch(fetchResultsRejected("Authentication error. Please refresh the page and try again."));
        dispatch(unlockChatInput());
      }
      return;
    }

    try {
      const delegatedToken = await acquireDelegatedGraphToken();
      activity = attachIdentityToActivity(activity, delegatedToken);
      console.log("✅ Attached user identity to activity");
    } catch (tokenError) {
      console.error("❌ Failed to acquire delegated token — cannot send without identity:", tokenError);
      if (!isControlMessage) {
        dispatch(setBotTyping(false));
        dispatch(fetchResultsRejected("Authentication error. Please refresh the page and try again."));
        dispatch(unlockChatInput());
      }
      return;
    }

    directLine
      .postActivity(activity)
      .subscribe({
        error: () => {
          if (!isControlMessage) {
            dispatch(setBotTyping(false));
            dispatch(fetchResultsRejected("Error: Failed to send message."));
            dispatch(unlockChatInput());
          }
        },
      });
  } catch (err) {
    if (!isControlMessage) {
      dispatch(setBotTyping(false));
      dispatch(fetchResultsRejected((err as Error).message || 'Unknown error'));
      dispatch(unlockChatInput());
    }
  }
};

/**
 * Thunk to send upload batch complete control message to backend.
 * This creates a conversation turn to persist file upload events.
 * @param files - Array of { fileName, fileSize, docId } for completed uploads
 */
export const sendUploadBatchComplete = (files: Array<{ fileName?: string; fileSize?: number; docId?: string }>) => async (dispatch: any, getState: any) => {
  const state = getState();
  const projectId = state.projectSlice?.currentProjectId;

  if (!projectId || files.length === 0) {
    console.log('⚠️ [UPLOAD_BATCH] Skipping batch complete - no project ID or no files');
    return;
  }

  try {
    // Pre-send health check: handle dead or still-connecting DirectLine
    if (!isDirectLineHealthy()) {
      if (isDirectLineTerminal()) {
        await dispatch(reconnectDirectLine());
      } else {
        await ensureDirectLineOnline();
      }
    }

    const msalAccount = getCurrentMsalAccount();
    const directLine = await getDirectLine(null, msalAccount);

    const activity = {
      from: { id: "user", role: "user" as const },
      type: "message" as const,
      text: "CONTROL_UPLOAD_BATCH_COMPLETE",
      channelData: {
        message_type: "control",
        control_event: "upload_batch_complete",
        project_id: projectId,
        files: files,
      },
    };

    directLine
      .postActivity(activity)
      .subscribe({
        next: () => console.log('✅ [UPLOAD_BATCH] Upload batch complete message sent', { fileCount: files.length }),
        error: (err: any) => console.error('❌ [UPLOAD_BATCH] Failed to send upload batch complete:', err),
      });
  } catch (err) {
    console.error('❌ [UPLOAD_BATCH] Error sending upload batch complete:', err);
  }
};

/**
 * Thunk to start a new project.
 * This orchestrates the complete reset flow:
 * 1. Reset ALL Redux state synchronously (messages, results, layout, project context)
 * 2. Reset module-level state (listener, activity IDs, DirectLine)
 * 3. Set temporary project state
 * 4. Reinitialize chat listener (which will trigger bootstrap)
 *
 * IMPORTANT: All synchronous cleanup (Step 1) runs BEFORE any async operations
 * to ensure workspace is always cleared even if async steps fail (e.g., after auth timeout).
 */
export const startNewProject = () => async (dispatch: any, getState: any) => {
  console.log('🔄 [NEW CHAT] startNewProject thunk called');

  // Acquire project switch lock — aborts any in-flight project switch
  const signal = acquireProjectSwitchLock();

  // Step 0: Cancel any ongoing conversation load immediately and clear loading state
  console.log('🔄 [NEW CHAT] Step 0: Cancelling any ongoing conversation load');
  cancelOngoingConversationLoad();
  dispatch(setChatHistoryLoading(false));
  dispatch(setChatHistoryError(null));

  // Step 1: Reset ALL Redux state synchronously BEFORE any async operations
  // This ensures workspace is always cleared even if subsequent async steps fail
  // (e.g., after auth timeout or DirectLine connection issues)
  console.log('🔄 [NEW CHAT] Step 1: Resetting all Redux state synchronously');
  dispatch(resetChatForNewProject());
  dispatch(clearResults());
  dispatch(closeAllDocumentPreviews());
  dispatch(setLayoutMode("chat"));


  try {
    // Step 2: Reset module-level state (listener, DirectLine, activity IDs)
    // This is async and can fail, but workspace is already clean
    console.log('🔄 [NEW CHAT] Step 2: Resetting module-level state');
    await resetChatModuleState();
    if (signal.aborted) {
      console.log('🔒 [NEW CHAT] Superseded after resetChatModuleState — aborting');
      return;
    }

    // Step 3: Set temporary project state with auto-generated date-based name
    const now = new Date();
    const day = now.getDate();
    const month = now.getMonth() + 1; // JS months are 0-indexed
    const year = String(now.getFullYear()).slice(-2); // Last 2 digits
    const autoName = `New ${day}/${month}/${year}`;
    const tempProjectId = `temp-${now.getTime()}-${Math.random().toString(36).slice(-4)}`;
    const placeholderProject = {
      id: tempProjectId,
      name: autoName,
      summary: '',
      isTemporary: true,
    };

    console.log('🔄 [NEW CHAT] Step 3: Setting temporary project state:', autoName, tempProjectId);
    dispatch(setCurrentProject(placeholderProject));
    dispatch(
      upsertUserProject({
        project: { ...placeholderProject },
        prepend: true,
      })
    );
    dispatch(markInitialized(false));

    // Step 4: Reinitialize chat listener (will trigger bootstrap automatically)
    console.log('🔄 [NEW CHAT] Step 4: Reinitializing chat listener');
    await dispatch(initializeChatListener(signal));
    if (signal.aborted) {
      console.log('🔒 [NEW CHAT] Superseded after initializeChatListener — aborting');
      return;
    }
    console.log('✅ [NEW CHAT] startNewProject completed successfully');
  } catch (error) {
    if (signal.aborted) return;
    console.error('❌ [NEW CHAT] startNewProject failed:', error);
    dispatch(
      setBootstrapError(`Failed to start new project: ${(error as Error).message || 'Unknown error'}`)
    );
  }
};

// Thunk to initialize core agent bootstrap
export const initializeCoreAgentBootstrap =
  () => async (dispatch: any, getState: any) => {
    console.log('🚀 [BOOTSTRAP] initializeCoreAgentBootstrap called');
    const state = getState();
    const { bootstrapInitialized } = state.chatSlice;

    // Only run once per session
    if (bootstrapInitialized) {
      console.log('⚠️ [BOOTSTRAP] Bootstrap already initialized - returning early');
      return;
    }

    try {
      console.log('🚀 [BOOTSTRAP] Setting bootstrapInitialized flag to true');
      dispatch(setBootstrapInitialized(true));

      // Phase 2: Get MSAL account FIRST (needed for APIM authentication)
      console.log('🚀 [BOOTSTRAP] Getting MSAL account');
      const msalAccount = getCurrentMsalAccount();

      // Wait for conversation metadata (Phase 2: now passes MSAL account)
      console.log('🚀 [BOOTSTRAP] Getting conversation metadata');
      const conversationData = await getConversationMetadata(msalAccount);
      console.log('🚀 [BOOTSTRAP] Conversation metadata received:', conversationData);

      // Get user info from auth state
      const { userName, userEmail } = state.userAuthAndAdminSlice;
      const { mcpEnvironment, dataVersion, selectedUseCase } = state.chatSlice;
      // Get project type from loaded project (fallback for reloaded projects)
      const projectType = selectCurrentProjectType(state);
      // Use selectedUseCase (new project) or projectType (reloaded project)
      const effectiveUseCase = selectedUseCase || projectType;

      // Get current project ID and name from project slice
      const currentProjectId = state.projectSlice?.currentProjectId;
      const currentProjectName = state.projectSlice?.currentProject?.name;
      console.log('🚀 [BOOTSTRAP] User info:', { userName, userEmail, currentProjectId, currentProjectName, effectiveUseCase });

      // Initialize DirectLine with identity (should already be initialized by getConversationMetadata)
      console.log('🚀 [BOOTSTRAP] Getting DirectLine connection');
      const directLine = await getDirectLine(null, msalAccount);

      const channelData: Record<string, unknown> = {
        message_type: "control",
        control_event: "bootstrap",
        username: userName || userEmail,
        user_email: userEmail,
        mcp_environment: mcpEnvironment,
        data_version: dataVersion,
        use_case: effectiveUseCase,  // Use selectedUseCase (new) or projectType (reloaded)
      };

      // Include project_id if available for conversation rehydration
      // BUT only if it's a permanent project - temporary projects should get a new UUID from backend
      if (currentProjectId && !currentProjectId.startsWith('temp-')) {
        console.log('🚀 [BOOTSTRAP] Including project_id in bootstrap (permanent project):', currentProjectId);
        channelData.project_id = currentProjectId;
      } else if (currentProjectId && currentProjectId.startsWith('temp-')) {
        console.log('🚀 [BOOTSTRAP] Skipping project_id in bootstrap (temporary project - backend will generate UUID):', currentProjectId);
        // Include the project name so backend can use it for the new permanent project
        if (currentProjectName) {
          console.log('🚀 [BOOTSTRAP] Including project_label for new project:', currentProjectName);
          channelData.project_label = currentProjectName;
        }
      }

      // Phase 1: Acquire delegated token and attach identity to bootstrap
      let activity: any = {
        from: { role: "user" },
        type: "message",
        text: "CONTROL_CORE_AGENT_BOOTSTRAP",
        channelData,
      };

      try {
        console.log('🚀 [BOOTSTRAP] Acquiring delegated Graph token');
        const delegatedToken = await acquireDelegatedGraphToken();
        activity = attachIdentityToActivity(activity, delegatedToken);
        console.log("✅ [BOOTSTRAP] Attached user identity to bootstrap message");
      } catch (tokenError) {
        console.warn("⚠️ [BOOTSTRAP] Failed to acquire delegated token for bootstrap - sending without identity:", tokenError);
      }

      console.log('🚀 [BOOTSTRAP] Posting bootstrap activity to DirectLine');
      directLine
        .postActivity(activity)
        .subscribe({
          next: (id) => {
            console.log('✅ [BOOTSTRAP] Bootstrap activity posted successfully, ID:', id);
          },
          error: (err) => {
            console.error('❌ [BOOTSTRAP] Failed to post bootstrap activity:', err);
            dispatch(setBootstrapError("Failed to send bootstrap message"));
          },
        });
    } catch (err) {
      console.error('❌ [BOOTSTRAP] Bootstrap initialization failed:', err);
      dispatch(setBootstrapError((err as Error).message || 'Unknown error'));
    }
  };

export const initializeChatListener = (switchSignal?: AbortSignal) => async (dispatch: any, getState: any) => {
  console.log('🎧 [LISTENER] initializeChatListener called');
  console.log('🎧 [LISTENER] Current state - isListenerInitialized:', isListenerInitialized, 'sessionId:', currentSessionId);
  console.log('🎧 [LISTENER] activitySubscription status:', activitySubscription ? 'exists' : 'null');
  console.log('🎧 [LISTENER] handledActivityIds count:', handledActivityIds.size);

  // Detect if this is a new page load by checking if session ID changed
  // This handles module persistence across HMR/page refreshes
  if (
    lastSessionId !== null &&
    lastSessionId !== currentSessionId &&
    isListenerInitialized
  ) {
    console.log('🎧 [LISTENER] Session ID changed - resetting listener state');
    isListenerInitialized = false;
    handledActivityIds.clear();
    // Also reset DirectLine and conversation metadata
    const { resetDirectLine } = await import("./directLlineClient");
    resetDirectLine();
  }

  lastSessionId = currentSessionId;

  if (isListenerInitialized) {
    console.log('⚠️ [LISTENER] Listener already initialized - returning early');
    console.log('⚠️ [LISTENER] If you need to reinitialize, call resetChatModuleState() first');
    return;
  }

  console.log('✅ [LISTENER] Guard check passed - proceeding with initialization');

  let directLine;

  try {
    console.log('🎧 [LISTENER] Step 1: Getting MSAL account and connecting to DirectLine');
    // Phase 1: Get MSAL account for DirectLine initialization with identity
    const msalAccount = getCurrentMsalAccount();

    // Step 1: Wait for DirectLine connection to be online
    directLine = await ensureDirectLineOnline(15000, null, msalAccount);

    // Check if this switch was superseded while we waited for DirectLine
    if (switchSignal?.aborted) {
      console.log('🔒 [LISTENER] Superseded after ensureDirectLineOnline — cleaning up');
      const { resetDirectLine } = await import("./directLlineClient");
      await resetDirectLine();
      isListenerInitialized = false;
      return;
    }

    console.log('✅ [LISTENER] DirectLine connection established');
  } catch (err) {
    console.error('❌ [LISTENER] Failed to establish DirectLine connection:', err);
    try {
      const { resetDirectLine } = await import("./directLlineClient");
      await resetDirectLine();
    } catch (resetErr) {}
    // Reset flag so we can retry on subsequent attempts
    isListenerInitialized = false;
    handledActivityIds.clear();
    dispatch(
      setBootstrapError(
        "Unable to connect to the chat service. Please refresh and try again."
      )
    );
    return;
  }

  console.log('🎧 [LISTENER] Marking listener as initialized');
  isListenerInitialized = true;

  // Capture and store conversation ID when available
  if (directLine.conversationId) {
    console.log('🎧 [LISTENER] DirectLine conversationId:', directLine.conversationId);
    dispatch(setConversationId(directLine.conversationId));
  }

  // Step 2: Set up DURABLE activity subscription BEFORE health probe to catch all messages
  // This subscription will remain active for the lifetime of the chat session
  const directLineUserId = directLine?.userId;
  console.log('🎧 [LISTENER] DirectLine userId:', directLineUserId);

  console.log('🎧 [LISTENER] Setting up activity subscription');
  activitySubscription = directLine.activity$.subscribe({
    next: (activity) => {
      //

      // Only process 'message' activities (streaming chunks are now sent as messages)
      if (!activity || activity.type !== "message") {
        return;
      }

      const fromId = activity.from?.id;
      if (!fromId) {
        return; // FIX: Return early if no fromId
      }

      // Skip echoes of user activities that DirectLine returns to the client
      // DirectLine echoes user messages back with the same user ID
      if (directLineUserId && fromId === directLineUserId) {
        return;
      }

      const channelData = activity.channelData || {};
      if (channelData.message_type === "user") {
        return;
      }
      if (activity.id && handledActivityIds.has(activity.id)) {
        return;
      }
      if (activity.id) {
        handledActivityIds.add(activity.id);
      }


      //

      // Check if this is a file processing notification control message
      const notification = channelData.notification;

      if (notification && notification.type === "file_processing_update") {
        // Get current state to retrieve project ID
        const state = window.store?.getState();
        const currentProjectId = state?.projectSlice?.currentProjectId;

        if (
          notification.project_id &&
          notification.project_id === currentProjectId
        ) {
          // Update upload table instead of showing chat message
          // Find the upload by filename and update its status
          const filename = notification.filename;
          const processingStatus = notification.processing_status;

          if (filename && processingStatus) {
            // Find the upload table message (message-based approach)
            const messages = state?.chatSlice?.messages || [];
            const uploadTableMessage = messages.find(
              (msg: ChatMessage) => msg.type === "upload-table" && !msg.allCompleted
            );

            // Find the upload entry by filename within the message
            const uploadEntry = uploadTableMessage?.uploads
              ? Object.entries(uploadTableMessage.uploads as Record<string, import('./types/redux').UploadItem>).find(
                  ([_id, upload]) => upload.fileName === filename
                )
              : undefined;

            if (uploadEntry) {
              const [uploadId] = uploadEntry;

              // Map processing status to upload table status
              let statusText: string;
              let completed: boolean;
              let error: boolean;

              if (processingStatus === "processing") {
                statusText = "Processing document...";
                completed = false;
                error = false;
              } else if (processingStatus === "processed") {
                statusText = "Ready";
                completed = true;
                error = false;
              } else if (processingStatus === "failed") {
                // Ensure error_message is a string (not [Object object])
                const errMsg = notification.error_message;
                statusText = typeof errMsg === 'string' ? errMsg : (errMsg ? String(errMsg) : "Processing failed");
                completed = true;
                error = true;
              } else {
                // Unknown status - default to waiting
                statusText = "Waiting for processing...";
                completed = false;
                error = false;
              }

              dispatch(updateUploadInTable({
                uploadId,
                status: statusText,
                completed,
                error,
                docId: notification.doc_id  // Capture document ID from processing notification
              }));

              // After updating, check if this was the last file to complete
              // If all uploads are now "Ready", send batch complete to persist the turn
              if (processingStatus === "processed") {
                // Get fresh state after the reducer update
                const updatedState = window.store?.getState();
                const updatedMessages = updatedState?.chatSlice?.messages || [];
                // Find the upload-table that contains this specific uploadId (not just any upload-table)
                const updatedUploadTable = updatedMessages.find(
                  (msg: ChatMessage) =>
                    msg.type === "upload-table" &&
                    msg.uploads &&
                    (msg.uploads as Record<string, import('./types/redux').UploadItem>)[uploadId]
                );

                if (updatedUploadTable?.allCompleted) {
                  const uploads = updatedUploadTable.uploads as Record<string, import('./types/redux').UploadItem>;
                  const allUploads = Object.values(uploads);

                  // Check if all successful uploads are now "Ready"
                  const allProcessed = allUploads
                    .filter((u: import('./types/redux').UploadItem) => u.completed && !u.error)
                    .every((u: import('./types/redux').UploadItem) => u.status === "Ready");

                  // Check if we've already sent the batch complete for this table
                  const batchSentKey = `upload-batch-sent-${updatedUploadTable.clientMessageId}`;
                  const alreadySent = (window as any)[batchSentKey];

                  if (allProcessed && !alreadySent) {
                    // Mark as sent to prevent duplicate sends
                    (window as any)[batchSentKey] = true;

                    // Collect completed files with their metadata
                    const completedFiles = allUploads
                      .filter((u: import('./types/redux').UploadItem) => u.completed && !u.error)
                      .map((u: import('./types/redux').UploadItem) => ({
                        fileName: u.fileName,
                        fileSize: u.fileSize,
                        docId: u.docId,
                      }));

                    if (completedFiles.length > 0) {
                      console.log('📦 [UPLOAD_BATCH] All files processed, sending batch complete', {
                        fileCount: completedFiles.length,
                        files: completedFiles.map(f => f.fileName)
                      });
                      dispatch(sendUploadBatchComplete(completedFiles));
                    }
                  }
                }
              }
            }
          }
        }

        // Don't process this as a normal message
        return;
      }

      if (notification && notification.type === "draft_document_update") {
        // Get current state to retrieve project ID
        const state = window.store?.getState();
        const currentProjectId = state?.projectSlice?.currentProjectId;

        if (
          notification.project_id &&
          notification.project_id === currentProjectId
        ) {
          // Show notification message in chat
          dispatch(
            addMessage({
              sender: "chatbot",
              text: activity.text,
            })
          );
        } else {
        }

        // Don't process this as a normal message
        return;
      }

      if (notification && notification.type === "project_checkpoint_update") {
        // Get current state to retrieve project ID
        const state = window.store?.getState();
        const currentProjectId = state?.projectSlice?.currentProjectId;

        if (
          notification.project_id &&
          notification.project_id === currentProjectId
        ) {
          // Update project metadata in Redux store
          dispatch(
            updateProjectMetadata({
              name: notification.name,
              summary: notification.summary,
            })
          );
        } else {
        }

        // Don't process this as a normal message (no chat message shown)
        return;
      }

      // ─── CONTROL MESSAGE HANDLER ───
      // Handle control messages first (health_ack, bootstrap echoes, health_probe echoes, etc.)
      if (channelData.message_type === "control") {
        const controlEvent = channelData.control_event;

        // Filter out user-sent control message echoes (bootstrap, health_probe)
        // These should never appear in the chat UI
        if (
          controlEvent === CONTROL_EVENT_BOOTSTRAP ||
          controlEvent === CONTROL_EVENT_HEALTH_PROBE
        ) {
          return; // Don't process further
        }

        // Handle health_ack from backend
        if (controlEvent === CONTROL_EVENT_HEALTH_ACK) {
          // Health probe acknowledgement - notify the health probe helper
          if (process.env.NODE_ENV === "development") {
          }
          notifyHealthAck();
          return; // Don't process further
        }

        // Filter out CONTROL_FEEDBACK echoes (thumbs up/down feedback messages)
        // These are sent from the UI and echoed back - they should not be processed
        if (activity.text && activity.text.startsWith("CONTROL_FEEDBACK:")) {
          return; // Don't process further
        }

        // Other control messages fall through to regular handling
      }

      // ─── NEW MESSAGE TYPE TAXONOMY HANDLER ───
      // Switch on channelData.message_type instead of parsing JSON from text
      const messageType = channelData.message_type;

      // Handle different message types
      switch (messageType) {
        case MESSAGE_TYPE_BOOTSTRAP_ACK: {
          console.log('✅ [BOOTSTRAP ACK] Received bootstrap acknowledgement');
          const payload = channelData.payload || {};
          console.log('✅ [BOOTSTRAP ACK] Payload:', payload);

          // Get current project ID from state to check if we're rehydrating
          const currentState = window.store?.getState();
          const existingProjectId = currentState?.projectSlice?.currentProjectId;

          // Only set project ID if we don't already have one set (i.e., new project creation)
          // When rehydrating an existing project, we don't want bootstrap_ack to overwrite
          // the project ID that was set from the URL
          if (!existingProjectId || existingProjectId.startsWith('temp-')) {
            console.log('✅ [BOOTSTRAP ACK] Setting project ID (no existing permanent project):', payload.projectId);
            dispatch(setProjectId(payload.projectId));
          } else {
            console.log('✅ [BOOTSTRAP ACK] Keeping existing project ID (rehydration mode):', existingProjectId);
            // Verify the backend acknowledged the same project we sent
            if (payload.projectId && payload.projectId !== existingProjectId) {
              console.warn('⚠️ [BOOTSTRAP ACK] Backend returned different project ID than expected:',
                { expected: existingProjectId, received: payload.projectId });
            }
          }

          console.log('✅ [BOOTSTRAP ACK] Marking project as initialized');
          dispatch(markInitialized());

          // If project metadata (name/summary) is present, update it
          if (payload.name || payload.summary) {
            console.log('✅ [BOOTSTRAP ACK] Updating project metadata:', { name: payload.name, summary: payload.summary });
            dispatch(
              updateProjectMetadata({
                name: payload.name,
                summary: payload.summary,
              })
            );
          }

          console.log('✅ [BOOTSTRAP ACK] Bootstrap acknowledgement processed successfully');
          return; // Don't process further
        }

        case MESSAGE_TYPE_BOOTSTRAP_ERROR: {
          dispatch(
            setBootstrapError(
              channelData.payload?.error || "Unknown bootstrap error"
            )
          );
          dispatch(unlockChatInput());
          return; // Don't process further
        }

        case MESSAGE_TYPE_RESULTS_PAYLOAD_CHUNK: {
          const chunkId = channelData.chunkId;
          const chunkIndex = channelData.chunkIndex;
          const chunkCount = channelData.chunkCount;
          const sizeBytes = channelData.sizeBytes;
          const turnId = channelData.turnId;
          const projectId = channelData.projectId;
          const payload = channelData.payload || {};

          console.log(`📦 [CHUNK] Received chunk ${chunkIndex + 1}/${chunkCount} for chunkId ${chunkId}`);

          // Get or create chunk accumulator for this chunkId (module-scoped Map)
          if (!chunkAccumulatorsByChunkId.has(chunkId)) {
            // Initialize new accumulator
            chunkAccumulatorsByChunkId.set(chunkId, {
              chunks: [],
              expected: chunkCount,
              receivedBytes: 0,
              turnId,
              projectId,
            });
          }

          const accumulator = chunkAccumulatorsByChunkId.get(chunkId)!;

          // Store chunk at correct index
          accumulator.chunks[chunkIndex] = {
            chunkIndex,
            payload: {
              results: payload.results || [],
              documents: payload.documents || [],
              facts: payload.facts || [],
            },
            sizeBytes,
          };
          accumulator.receivedBytes += sizeBytes;

          const receivedCount = accumulator.chunks.filter(c => c).length;
          console.log(`📦 [CHUNK] Stored chunk ${chunkIndex}, received ${receivedCount}/${accumulator.expected}`);

          // Check if all chunks have arrived (count actual entries, not array length)
          const allChunksReceived = receivedCount === accumulator.expected;

          if (allChunksReceived) {
            console.log(`✅ [CHUNK] All chunks received for chunkId ${chunkId}, reassembling...`);

            // Concatenate all chunks in order
            const assembledResults: unknown[] = [];
            const assembledDocuments: unknown[] = [];
            const assembledFacts: unknown[] = [];

            for (const chunk of accumulator.chunks) {
              if (!chunk || !chunk.payload) {
                console.error(`❌ [CHUNK] Missing chunk in accumulator during reassembly`);
                continue;
              }
              assembledResults.push(...chunk.payload.results);
              assembledDocuments.push(...chunk.payload.documents);
              assembledFacts.push(...chunk.payload.facts);
            }

            console.log(`✅ [CHUNK] Reassembled payload: ${assembledResults.length} results, ${assembledDocuments.length} docs, ${assembledFacts.length} facts`);
            console.log(`✅ [CHUNK] Total bytes: ${accumulator.receivedBytes}`);

            // Validate chunk count
            if (accumulator.chunks.length !== chunkCount) {
              console.error(`❌ [CHUNK] Chunk count mismatch: expected ${chunkCount}, got ${accumulator.chunks.length}`);
            }

            // Update project ID if present
            if (projectId) {
              dispatch(setProjectId(projectId));
            }

            // Dispatch assembled results to ResultsSlice.
            // Always dispatch — even when empty — so stale deals/docs from prior turns are cleared.
            // Note: facts are merged (not replaced) by setResults, which is intentional.
            const hasResults =
              assembledResults.length > 0 ||
              assembledDocuments.length > 0 ||
              assembledFacts.length > 0;

            const inlineDealsFlag = getFeatureFlag("inlineDeals");
            dispatch(setResults({
              results: assembledResults,
              documents: assembledDocuments,
              facts: assembledFacts,
              turnId: accumulator.turnId,
              inlineDealsFlag,
            }));
            if (hasResults) {
              // When inline deals flag is ON, only open workspace for facts (doc Q&A).
              // Documents returned alongside deals are accessible via the deal detail
              // panel's Docs tab, so they shouldn't auto-open the workspace.
              const hasNonDealResults = inlineDealsFlag
                ? assembledFacts.length > 0
                : true;
              if (hasNonDealResults) {
                dispatch(setLayoutMode("workspace"));
              }
            }

            // Clean up accumulator for this chunkId
            chunkAccumulatorsByChunkId.delete(chunkId);
            console.log(`🧹 [CHUNK] Cleaned up accumulator for chunkId ${chunkId}`);
          }

          return; // Don't process further
        }

        case MESSAGE_TYPE_RESULTS_PAYLOAD: {
          const payload = channelData.payload || {};
          const results = payload.results || [];
          const documents = payload.documents || [];
          const facts = payload.facts || [];
          const turnId = channelData.turnId;

          // DEBUG: Log raw results payload to trace missing fields
          console.log("🔍 [RESULTS_PAYLOAD] Raw payload.results:", JSON.stringify(results.slice(0, 2), null, 2));
          if (results.length > 0) {
            const sampleDeal = results[0];
            console.log("🔍 [RESULTS_PAYLOAD] Sample deal fields:", {
              dealName: sampleDeal.dealName,
              year: sampleDeal.year,
              investmentYear: sampleDeal.investmentYear,
              stage: sampleDeal.stage,
              dealStage: sampleDeal.dealStage,
              team: sampleDeal.team,
              description: sampleDeal.description,
              business: sampleDeal.business,
            });
          }

          // Update project ID if present
          if (payload.projectId) {
            dispatch(setProjectId(payload.projectId));
          }

          // Phase 3: Evidence data (facts, documents, results) goes to central ResultsSlice
          // Messages only store textual/chat metadata
          // Always dispatch — even when empty — so stale deals/docs from prior turns are cleared.
          // Note: facts are merged (not replaced) by setResults, which is intentional.
          const hasResults =
            results.length > 0 || documents.length > 0 || facts.length > 0;

          const inlineDealsFlag = getFeatureFlag("inlineDeals");
          dispatch(setResults({ results, documents, facts, turnId, inlineDealsFlag }));
          if (hasResults) {
            const hasNonDealResults = inlineDealsFlag
              ? facts.length > 0
              : true;
            if (hasNonDealResults) {
              dispatch(setLayoutMode("workspace"));
            }
          }

          return; // Don't process further
        }

        case MESSAGE_TYPE_FACTS_STREAM_ITEM: {
          // Handle individually streamed facts (sent after results/docs to avoid size limits)
          const fact = channelData.fact;
          const factIndex = channelData.factIndex;
          const factCount = channelData.factCount;

          if (fact) {
            // Log progress periodically (every 10 facts or first/last)
            if (factIndex === 0 || factIndex === factCount - 1 || factIndex % 10 === 0) {
              console.log(`📥 [FACT] Received fact ${factIndex + 1}/${factCount}`);
            }

            // Append fact to ResultsSlice
            dispatch(appendFact(fact));

            // Log completion
            if (factIndex === factCount - 1) {
              console.log(`✅ [FACT] All ${factCount} facts received`);
            }
          }

          return; // Don't process further
        }

        case MESSAGE_TYPE_CHAT_RESPONSE_CHUNK: {
          // Turn off typing indicator when first chunk arrives
          dispatch(setBotTyping(false));

          const turnId = channelData.turnId;
          const sequence = channelData.sequence;
          const chunkText = activity.text;
          // Bot Framework streaming pattern uses streamType to indicate accumulated text mode
          const streamType = channelData.streamType;
          const isAccumulatedText = streamType === 'streaming' || streamType === 'final';

          if (!turnId || sequence === undefined || sequence === null) {
            console.error('❌ [CHUNK] Missing required fields:', { turnId, sequence });
            return;
          }

          // Phase 8: Create placeholder message in chatSlice.messages if it doesn't exist
          // This allows the UI to render the message while the new streaming system animates it
          const state = getState();
          const existingMessage = state?.chatSlice?.messages?.find(
            msg => msg.turnId === turnId
          );

          if (!existingMessage) {
            // Create placeholder bot message that the UI will render
            dispatch(addMessage({
              sender: "chatbot",
              text: "", // Empty text - will be populated from streaming system
              turnId,
            }));
          }

          // Phase 6: Dispatch to new streaming slice
          // Pass isAccumulatedText flag to determine append vs replace behavior
          dispatch(
            newMessageChunkReceived({
              messageId: turnId,
              chunk: chunkText,
              index: sequence,
              isAccumulatedText,
            })
          );

          // If this is the final chunk, finalize the message
          if (streamType === 'final') {
            console.log('[FINALIZE] Received final streaming chunk, finalizing message:', turnId);
            dispatch(newFinalizeMessage({ messageId: turnId }));

            // Unlock chat input since streaming is complete
            dispatch(unlockChatInput());
          }

          return; // Don't process further
        }

        case MESSAGE_TYPE_CHAT_RESPONSE: {
          dispatch(setBotTyping(false));

          const turnId = channelData.turnId;
          const state = getState();
          const suggestedActions = channelData.suggested_actions;

          // Phase 8: Check if this message exists in the streaming slice
          const streamingMessage = state?.streaming?.messages?.[turnId];

          if (turnId && streamingMessage) {
            // Phase 8: Finalize message in new streaming slice
            console.log('[FINALIZE] Dispatching finalizeMessage for turnId:', turnId);
            dispatch(newFinalizeMessage({ messageId: turnId }));

            // Phase 8: Copy final displayedText to message.text for persistence
            // displayedText has parsed [[FACT:xxx]] placeholders from the streaming parser.
            // fullText has raw ^fid=xxx^ markers — apply migrateMessageContent so that
            // persisted message.text always uses the [[FACT:xxx]] format (fixes prior-turn
            // citations becoming non-clickable after a new turn overwrites streaming state).
            const finalText = streamingMessage.displayedText
              || migrateMessageContent(streamingMessage.fullText || '');
            const messageIndex = state.chatSlice.messages.findIndex(
              (msg: any) => msg.turnId === turnId
            );

            // Always update suggested_actions if we have them, regardless of finalText
            if (messageIndex !== -1) {
              dispatch(updateMessageText({ index: messageIndex, text: finalText || undefined, suggested_actions: suggestedActions }));
            }
          } else if (turnId) {
            // Non-streaming fallback: add message with suggested_actions if present
            dispatch(
              addMessage({
                sender: "chatbot",
                text: activity.text || "",
                turnId,
                suggested_actions: suggestedActions,
              })
            );
            if (suggestedActions && Array.isArray(suggestedActions)) {
              console.log('[DealOS] 📋 Received suggested_actions (non-stream):', suggestedActions);
            }
          }

          // Handle document preview refresh for any preview_document actions
          // When autoRefresh is true, we refresh by documentName (handles docId changes)
          // Otherwise, we invalidate by docId (for same-docId refreshes)
          console.log('[DealOS] 🔄 Processing suggested_actions for preview refresh:', suggestedActions);
          if (suggestedActions && Array.isArray(suggestedActions)) {
            suggestedActions.forEach((action: any) => {
              console.log('[DealOS] 🔄 Checking action:', action.type, action.document?.docId, 'autoRefresh:', action.autoRefresh);
              if (action.type === 'preview_document' && action.document?.docId) {
                if (action.autoRefresh && action.document?.documentName) {
                  // Auto-refresh: find open document by name and replace it with new document
                  // This handles cases where docId changes between edits
                  console.log('[DealOS] 🔄 Dispatching refreshDocumentByName for:', action.document.documentName);
                  dispatch(refreshDocumentByName({
                    documentName: action.document.documentName,
                    newDocument: action.document
                  }));
                } else {
                  // Standard invalidation by docId
                  console.log('[DealOS] 🔄 Dispatching invalidateDocumentPreview for:', action.document.docId);
                  dispatch(invalidateDocumentPreview(action.document.docId));
                }
              }
            });
          }

          // Unlock input when stream completes (or immediately for non-streaming)
          if (channelData.streamStatus === "complete" || !streamingMessage) {
            dispatch(unlockChatInput());

            // If results status is still "loading" at turn completion, it means no
            // results_payload was sent this turn (e.g. viability check failed).
            // Revert to "succeeded" so the previous deals/docs remain visible
            // instead of showing loading skeletons indefinitely.
            const resultsStatus = getState().resultsSlice?.status;
            if (resultsStatus === "loading") {
              dispatch(revertLoadingToSucceeded());
            }

            // Clear paragraph selections after response is complete
            // This ensures the user can see which paragraphs were selected while reading the response
            const state = getState();
            const currentProjectId = state?.projectSlice?.currentProjectId;
            if (currentProjectId) {
              console.log(`🧹 [ChatSlice] Clearing paragraph selections after chat response complete for project ${currentProjectId}`);
              dispatch(clearProjectSelections({ projectId: currentProjectId }));
            }
          }

          // Update project ID if present
          if (channelData.projectId) {
            dispatch(setProjectId(channelData.projectId));
          }

          return; // Don't process further
        }

        case MESSAGE_TYPE_WORKFLOW_STATUS: {
          const status = channelData.status || {};
          const turnId = channelData.turnId;
          const statusSequence = channelData.statusSequence ?? 0;

          console.log('🔍 WORKFLOW_STATUS received:', {
            turnId,
            statusSequence,
            hasStatusTodoPlan: !!status.todoPlan,
            statusLabel: status.label,
            todoPlan: status.todoPlan
          });

          // Out-of-order handling: ignore status updates with sequence <= highest seen for this turn
          // Fire-and-forget can cause later statuses to arrive before earlier ones
          if (turnId) {
            const highestSeen = highestStatusSequenceByTurn.get(turnId) ?? -1;
            if (statusSequence <= highestSeen) {
              console.log(`⏭️ WORKFLOW_STATUS: Ignoring out-of-order status (seq=${statusSequence} <= highest=${highestSeen})`);
              return; // Ignore late arrival
            }
            highestStatusSequenceByTurn.set(turnId, statusSequence);
          }

          // Extract todoPlan from status if present
          if (status.todoPlan && turnId) {
            console.log('✅ Dispatching setTurnTodoPlan:', { turnId, plan: status.todoPlan });
            dispatch(
              setTurnTodoPlan({
                turnId,
                plan: status.todoPlan,
                scopeSummary: status.scopeSummary,
              })
            );
          } else {
            console.log('⚠️ Not dispatching setTurnTodoPlan:', {
              hasStatusTodoPlan: !!status.todoPlan,
              hasTurnId: !!turnId
            });
          }

          // If state is "complete" OR label is the special clear marker, clear the workflow status
          if (status.label === "_clear_status_") {
            dispatch(setWorkflowStatus(null));
          } else if (status.state === "complete") {
            dispatch(setWorkflowStatus(null));
            // Clear the todo plan for this turn when workflow completes
            if (turnId) {
              dispatch(clearTurnTodoPlan({ turnId }));
            }
            // Note: Paragraph selections are now cleared when the chat response arrives (MESSAGE_TYPE_CHAT_RESPONSE)
            // This ensures the user can still see which paragraphs were selected while the response is being generated
          } else {
            // Otherwise, update with the current action status
            // Include turnId so the UI can check if there's already a message for THIS turn
            dispatch(
              setWorkflowStatus({
                label: status.label,
                action: status.action,
                state: status.state || 'start',
                visible: true,
                turnId: turnId,
              })
            );

            // Trigger generation progress bar when generate_content action starts
            if (status.action === "generate_content" && status.state === "start") {
              const state = window.store?.getState();
              const currentProjectId = state?.projectSlice?.currentProjectId;
              if (currentProjectId) {
                dispatch(addGenerationProgress({ projectId: currentProjectId }));
              }
            }
          }

          return; // Don't process further
        }

        default: {
          // Fallback for legacy messages or unrecognized types

          dispatch(setBotTyping(false));

          try {
            // Try parsing as legacy JSON format
            const parsedData = JSON.parse(activity.text);

            // Handle legacy bootstrap acknowledgement
            if (parsedData.meta?.messageType === "bootstrapAck") {
              dispatch(setProjectId(parsedData.projectId));
              dispatch(markInitialized());

              if (parsedData.meta?.name || parsedData.meta?.summary) {
                dispatch(
                  updateProjectMetadata({
                    name: parsedData.meta.name,
                    summary: parsedData.meta.summary,
                  })
                );
              }
              return;
            }

            // Handle legacy bootstrap error
            if (parsedData.meta?.messageType === "bootstrapError") {
              dispatch(setBootstrapError(parsedData.meta.error));
              dispatch(unlockChatInput());
              return;
            }

            // Handle legacy combined response
            dispatch(
              addMessage({
                sender: "chatbot",
                text: parsedData.response,
                turnId: parsedData.turnId,
              })
            );

            if (parsedData.projectId) {
              dispatch(setProjectId(parsedData.projectId));
            }

            // Always dispatch setResults — even when empty — so stale
            // deals/docs from prior turns are cleared (mirrors results_payload path).
            const results = parsedData.results || [];
            const documents = parsedData.documents || [];
            const inlineDealsFlag = getFeatureFlag("inlineDeals");
            dispatch(setResults({ results, documents, turnId: parsedData.turnId, inlineDealsFlag }));

            const hasResults = results.length > 0 || documents.length > 0;
            if (hasResults) {
              // Legacy path has no facts — when flag ON, never auto-open workspace
              const hasNonDealResults = inlineDealsFlag
                ? false
                : true;
              if (hasNonDealResults) {
                dispatch(setLayoutMode("workspace"));
              }
            }

            // Unlock input after legacy response is processed
            dispatch(unlockChatInput());
          } catch (e) {
            // Ultimate fallback: treat as plain text
            dispatch(addMessage({ sender: "chatbot", text: activity.text }));
            dispatch(
              fetchResultsRejected(
                "Response from bot was not in the expected format."
              )
            );
            // Unlock input after error
            dispatch(unlockChatInput());
          }
        }
      }
    },
    error: (err) => {
      console.warn('⚠️ [LISTENER] Activity subscription error — connection lost, will reconnect on next send:', err);
      // Mark listener as uninitialized so reconnection can reinitialize it
      isListenerInitialized = false;
      // Don't show a blocking error — the pre-send health check in sendMessage
      // will detect the unhealthy connection and transparently reconnect.
      dispatch(unlockChatInput());
    },
    complete: () => {
      dispatch(unlockChatInput());
    },
  });

  // Step 3: Run health probe to verify backend is ready
  // The durable subscription above will catch the health_ack and resolve the probe
  console.log('🎧 [LISTENER] Step 3: Running health probe');
  try {
    await ensureDirectLineHealthy(directLine);
    console.log('✅ [LISTENER] Health probe successful');
  } catch (err) {
    // If superseded by a newer project switch, exit silently (not an error)
    if (switchSignal?.aborted) {
      console.log('🔒 [LISTENER] Superseded during health probe — cleaning up silently');
      if (activitySubscription) {
        activitySubscription.unsubscribe();
        activitySubscription = null;
      }
      isListenerInitialized = false;
      handledActivityIds.clear();
      return;
    }
    console.error('❌ [LISTENER] Health probe failed:', err);
    // Cleanup on health probe failure
    if (activitySubscription) {
      activitySubscription.unsubscribe();
      activitySubscription = null;
    }
    isListenerInitialized = false;
    handledActivityIds.clear();
    dispatch(
      setBootstrapError(
        "DirectLine health check failed. Please refresh and try again."
      )
    );
    return;
  }

  // Check if superseded after health probe completed successfully
  if (switchSignal?.aborted) {
    console.log('🔒 [LISTENER] Superseded after health probe — cleaning up');
    if (activitySubscription) {
      activitySubscription.unsubscribe();
      activitySubscription = null;
    }
    isListenerInitialized = false;
    handledActivityIds.clear();
    const { resetDirectLine } = await import("./directLlineClient");
    await resetDirectLine();
    return;
  }

  // Step 4: Initialize bootstrap AFTER health probe confirms backend is ready
  console.log('🎧 [LISTENER] Step 4: Checking authentication state for bootstrap');
  const state = getState();
  const isAuthenticated = state.userAuthAndAdminSlice?.isAuthenticated;
  console.log('🎧 [LISTENER] isAuthenticated:', isAuthenticated);
  if (isAuthenticated) {
    console.log('🎧 [LISTENER] Starting bootstrap initialization');
    dispatch(initializeCoreAgentBootstrap());
  } else {
    console.log('⚠️ [LISTENER] User not authenticated - skipping bootstrap');
  }
  console.log('✅ [LISTENER] initializeChatListener completed successfully');
  console.log('✅ [LISTENER] Final state - isListenerInitialized:', isListenerInitialized, 'subscription active:', !!activitySubscription);
};

/**
 * Thunk to rehydrate conversation history for a project
 * Fetches the historical turns from the backend and rebuilds the chat state
 *
 * Expected API response schema (from conversation_history.py):
 * {
 *   conversationId: string,
 *   turns: [
 *     {
 *       turnId: string,
 *       createdAt: datetime,
 *       userInputText: string,
 *       finalChatResponse: string,
 *       todoPlan: object | null,
 *       scopePlan: object | null,
 *       thumbsUp: boolean | null,
 *       thumbsDown: boolean | null,
 *       feedbackComment: string | null
 *     }
 *   ],
 *   outputsByTurn: {
 *     [turnId]: [
 *       {
 *         turnOutputId: string,
 *         outputType: string (e.g., 'results_payload'),
 *         content: { results, documents, facts },
 *         createdAt: datetime
 *       }
 *     ]
 *   },
 *   stateSnapshot: {
 *     dealRegistry: object | null,
 *     documentRegistry: object | null,
 *     facts: array | null
 *   },
 *   latestWorkspacePayload: {
 *     turnId: string,
 *     createdAt: datetime,
 *     results: array,
 *     documents: array,
 *     facts: array,
 *     projectId: string,
 *     source: 'results_payload' | 'final_chat_response'
 *   } | null
 * }
 *
 * Phase 3: UI rehydration now uses latestWorkspacePayload (the canonical snapshot)
 * instead of manually scanning outputsByTurn for results_payload entries.
 */
export const rehydrateConversation = (projectId: string) => async (dispatch: any, getState: any) => {
  console.log('🔄 [REHYDRATE] rehydrateConversation called for projectId:', projectId);

  // Skip rehydration for temporary projects (they have no conversation history yet)
  if (projectId.startsWith('temp-')) {
    console.log('🔄 [REHYDRATE] Temporary project detected - skipping API call');
    dispatch(setLayoutMode('chat'));
    dispatch(setChatHistoryLoading(false));
    return;
  }

  // Create AbortController for this load operation (cancels any prior load)
  const abortController = createConversationLoadAbortController();

  // Set loading state
  dispatch(setChatHistoryLoading(true));
  dispatch(setChatHistoryError(null));

  try {
    // Import projectService dynamically to avoid circular dependencies
    const { getConversationHistory } = await import('./services/projectService');

    console.log('🔄 [REHYDRATE] Fetching conversation history from API');
    const history = await getConversationHistory(projectId, abortController.signal);

    // Check if this request was cancelled (user switched to another project/chat)
    if (abortController.signal.aborted) {
      console.log('🛑 [REHYDRATE] Request was cancelled - aborting rehydration');
      return;
    }

    console.log('🔄 [REHYDRATE] Received history:', history);

    // Check if there are any turns to rehydrate
    if (!history.turns || history.turns.length === 0) {
      console.log('🔄 [REHYDRATE] No turns to rehydrate - keeping welcome message');
      // Reset to chat mode when no history exists
      dispatch(setLayoutMode('chat'));
      dispatch(setChatHistoryLoading(false));
      return;
    }

    // Build messages array from turns
    const rehydratedMessages: ChatMessage[] = [];
    const rehydratedTodoPlans: Record<string, unknown> = {};

    for (const turn of history.turns) {
      // Check if this is a file upload turn (persisted via upload_batch_complete)
      const fileUploadBatch = turn.mergedContext?.file_upload_batch;
      const isFileUploadTurn = turn.userInputText === "[FILE_UPLOAD_BATCH]" || fileUploadBatch;

      if (isFileUploadTurn && fileUploadBatch?.files) {
        // Create an upload-table message for rehydrated file uploads
        const uploads: Record<string, import('./types/redux').UploadItem> = {};
        const files = fileUploadBatch.files as Array<{ fileName?: string; fileSize?: number; docId?: string }>;

        files.forEach((file, index) => {
          const id = file.docId || `file-${index}`;
          uploads[id] = {
            id,
            fileName: file.fileName,
            fileSize: file.fileSize,
            status: "Complete",
            completed: true,
            error: false,
            progress: 100,
            docId: file.docId,
          };
        });

        rehydratedMessages.push({
          sender: "chatbot",
          type: "upload-table",
          turnId: turn.turnId,
          uploads,
          allCompleted: true,
          text: "",
          timestamp: turn.createdAt,
        } as ChatMessage);

        // Also add the summary text message
        if (turn.finalChatResponse) {
          rehydratedMessages.push({
            sender: "chatbot",
            text: migrateMessageContent(turn.finalChatResponse),
            timestamp: turn.createdAt,
            turnId: turn.turnId,
            type: "upload-summary",
          });
        }

        console.log('📦 [REHYDRATE] Restored file upload turn:', {
          turnId: turn.turnId,
          fileCount: files.length,
          files: files.map(f => f.fileName)
        });

        continue; // Skip regular message handling for file upload turns
      }

      // Add user message
      if (turn.userInputText) {
        rehydratedMessages.push({
          sender: "user",
          text: turn.userInputText,
          timestamp: turn.createdAt,
          turnId: turn.turnId,
        });
      }

      // Add bot message
      if (turn.finalChatResponse) {
        // Phase 8: Migrate old fact format (^fid=xxx^) to new format ([[FACT:xxx]])
        // This ensures backward compatibility with messages stored before the refactor
        const migratedText = migrateMessageContent(turn.finalChatResponse);

        rehydratedMessages.push({
          sender: "chatbot",
          text: migratedText,
          timestamp: turn.createdAt,
          turnId: turn.turnId,
          inlineOrder: [], // inlineOrder is not persisted, facts will be parsed from text
          // Restore suggested_actions (UI buttons like Preview Draft, Download DOCX)
          suggested_actions: turn.suggestedActions || undefined,
        });
      }

      // Store todo plan if present
      if (turn.todoPlan && turn.turnId) {
        rehydratedTodoPlans[turn.turnId] = {
          plan: turn.todoPlan,
          scopeSummary: turn.scopePlan, // Backend uses scopePlan, not scopeSummary
        };
      }
    }

    console.log('🔄 [REHYDRATE] Rehydrated messages:', rehydratedMessages.length);
    console.log('🔄 [REHYDRATE] Rehydrated todo plans:', Object.keys(rehydratedTodoPlans).length);

    // Replace messages with rehydrated history (without welcome message)
    // Use setMessages to directly replace the messages array, avoiding the welcome message
    // that resetChatForNewProject would add
    dispatch(setMessages(rehydratedMessages));

    // Unlock chat input since we're rehydrating an existing conversation
    // User has already "selected a use case" by having a conversation history
    dispatch(setUseCaseSelected(true));

    // Restore the selected use case from the project type so UI controls
    // (e.g. file upload disabled for deal search) remain correct after rehydration
    const projectType = selectCurrentProjectType(getState());
    if (projectType) {
      dispatch(setSelectedUseCase(projectType));
    }

    // Restore todo plans
    for (const [turnId, planData] of Object.entries(rehydratedTodoPlans)) {
      const plan = (planData as any).plan;
      const scopeSummary = (planData as any).scopeSummary;
      dispatch(setTurnTodoPlan({ turnId, plan, scopeSummary }));
    }

    // Phase 3a: Rehydrate ALL historical facts and documents from outputsByTurn
    // This ensures prior turn facts are available for inline badge rendering
    // and their documents are in the registry so citations can resolve
    // The setResults reducer merges facts and documents into their registries
    const outputsByTurn = history.outputsByTurn;
    let totalHistoricalFacts = 0;
    let totalHistoricalDocs = 0;

    if (outputsByTurn && typeof outputsByTurn === 'object') {
      for (const [turnId, outputs] of Object.entries(outputsByTurn)) {
        if (Array.isArray(outputs)) {
          for (const output of outputs) {
            if (output.outputType === 'results_payload' && output.content) {
              const facts = output.content.facts;
              const documents = output.content.documents;
              const hasFacts = Array.isArray(facts) && facts.length > 0;
              const hasDocs = Array.isArray(documents) && documents.length > 0;

              if (hasFacts || hasDocs) {
                if (hasFacts) totalHistoricalFacts += facts.length;
                if (hasDocs) totalHistoricalDocs += documents.length;
                // Dispatch facts and documents — setResults will:
                //   - Merge facts into state.facts
                //   - Merge documents into state.documentRegistry
                //   - Replace state.documents (overwritten by latestWorkspacePayload below)
                dispatch(setResults({
                  facts: hasFacts ? facts : undefined,
                  documents: hasDocs ? documents : undefined,
                  results: output.content.results,
                  turnId: turnId,
                  inlineDealsFlag: getFeatureFlag("inlineDeals"),
                }));
              }
            }
          }
        }
      }
      console.log('🔄 [REHYDRATE] Restored historical facts and documents from outputsByTurn:', {
        turnsWithOutputs: Object.keys(outputsByTurn).length,
        totalFacts: totalHistoricalFacts,
        totalDocs: totalHistoricalDocs
      });
    }

    // Phase 3b: Rehydrate workspace results from latestWorkspacePayload (authoritative source)
    // This sets the current workspace view (deals, documents) and adds any additional facts
    const latestWorkspacePayload = history.latestWorkspacePayload;

    if (latestWorkspacePayload) {
      const { results, documents, facts, turnId, source } = latestWorkspacePayload;

      console.log('🔄 [REHYDRATE] Restoring workspace from latestWorkspacePayload:', {
        turnId,
        source,
        resultsCount: results?.length || 0,
        documentsCount: documents?.length || 0,
        factsCount: facts?.length || 0
      });

      // Dispatch setResults with evidence from latestWorkspacePayload
      // This populates ResultsSlice with deals, documents, and facts
      // Facts will be merged with the historical facts already loaded above
      const inlineDealsFlag = getFeatureFlag("inlineDeals");
      dispatch(setResults({
        results: results || [],
        documents: documents || [],
        facts: facts || [],
        turnId: turnId,
        inlineDealsFlag,
      }));

      // Switch to workspace mode if we have evidence, otherwise stay in chat mode
      const hasEvidence = (results && results.length > 0) ||
                         (documents && documents.length > 0) ||
                         (facts && facts.length > 0);

      if (hasEvidence) {
        // When inline deals flag is ON, only open workspace for facts (doc Q&A).
        // Documents alongside deals are accessible via the deal detail panel.
        const hasNonDealEvidence = inlineDealsFlag
          ? (facts && facts.length > 0)
          : true;
        if (hasNonDealEvidence) {
          dispatch(setLayoutMode('workspace'));
        }
      } else {
        dispatch(setLayoutMode('chat'));
      }
    } else {
      // No latestWorkspacePayload found - this means the conversation genuinely has no workspace data
      // Do NOT fabricate placeholder deals - stay in chat mode per project guidelines
      console.log('🔄 [REHYDRATE] No latestWorkspacePayload found - staying in chat mode');
      dispatch(setLayoutMode('chat'));
    }

    // Phase 4: Check if there's an in-progress generation for this project
    // Only relevant for offer-letter projects (doc-drafter); skip for deal search to avoid unnecessary 404s
    const rehydrateProjectType = selectCurrentProjectType(getState());
    if (rehydrateProjectType === 'offer-letter') {
      try {
        const { authenticatedFetch } = await import('./utils/authErrorHandler');
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
        const statusResponse = await authenticatedFetch(
          `${apiBaseUrl}/api/doc-drafter/status/${projectId}`,
          { signal: abortController.signal }
        );

        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          const processingStatus = statusData.processing_status;

          // Restore progress bar for both in-progress and completed generation
          if (processingStatus === 'generating' || processingStatus === 'processing') {
            console.log('🔄 [REHYDRATE] Found in-progress generation, restoring progress bar');
            dispatch(addGenerationProgress({ projectId }));
          } else if (processingStatus === 'processed') {
            // Generation is complete - restore the progress bar in completed state
            console.log('🔄 [REHYDRATE] Found completed generation, restoring progress bar at 100%');
            dispatch(addGenerationProgress({ projectId }));
            // Immediately update to processed state to show "Document ready!"
            dispatch(updateGenerationStatus({ projectId, status: 'processed' }));
          }
        }
        // 404 means no generation started - that's fine, just skip
      } catch (statusError) {
        // Don't fail rehydration if status check fails - it's optional
        if (!(statusError instanceof Error && statusError.name === 'AbortError')) {
          console.log('🔄 [REHYDRATE] Generation status check failed (non-critical):', statusError);
        }
      }
    }

    console.log('✅ [REHYDRATE] Conversation rehydration complete');
    dispatch(setChatHistoryLoading(false));
  } catch (error) {
    // Handle abort errors gracefully - these are expected when user switches projects/chats
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('🛑 [REHYDRATE] Rehydration was cancelled (AbortError) - this is expected during project switch');
      // Don't set error state for intentional cancellations
      dispatch(setChatHistoryLoading(false));
      return;
    }

    // Handle 404 errors gracefully - just show fresh chat view (no error message needed)
    const errorMessage = error instanceof Error ? error.message : 'Failed to load conversation history';
    if (errorMessage.includes('404') || errorMessage.includes('not found')) {
      console.log('🔄 [REHYDRATE] Conversation not found (404) - showing fresh chat view');
      dispatch(setChatHistoryLoading(false));
      dispatch(setLayoutMode('chat'));
      return;
    }

    console.error('❌ [REHYDRATE] Failed to rehydrate conversation:', error);
    dispatch(setChatHistoryError(errorMessage));
    dispatch(setChatHistoryLoading(false));
    dispatch(setLayoutMode('chat'));
  }
};

// Selectors
/**
 * Select visible messages
 * Phase 3: All messages are visible now - no internal placeholders
 */
export const selectVisibleMessages = (state: RootState): ChatMessage[] => {
  return state.chatSlice?.messages || [];
};

/**
 * Get todo plan for a specific turn
 */
export const selectTurnTodoPlan = (state: RootState, turnId: string): unknown | null => {
  const turnData = state.chatSlice?.todoPlanByTurn?.[turnId];
  return turnData && typeof turnData === 'object' && 'plan' in turnData ? (turnData as any).plan : null;
};

/**
 * Get all todo plans
 */
export const selectAllTodoPlans = (state: RootState): Record<string, unknown> => {
  return state.chatSlice?.todoPlanByTurn || {};
};

/**
 * Get the current active turn ID (from pending streaming messages)
 */
export const selectCurrentTurnId = (state: RootState): string | null => {
  const pendingMessages = state.chatSlice?.pendingStreamingMessages || {};
  const turnIds = Object.keys(pendingMessages);
  if (turnIds.length > 0) {
    return turnIds[0] || null;
  }

  const todoPlans = state.chatSlice?.todoPlanByTurn || {};
  const planTurnIds = Object.keys(todoPlans);
  if (planTurnIds.length > 0) {
    return planTurnIds[planTurnIds.length - 1] || null;
  }

  return null;
};

/**
 * Get the current turn's todo plan (convenience selector)
 */
export const selectCurrentTurnTodoPlan = (state: RootState): unknown | null => {
  const currentTurnId = selectCurrentTurnId(state);
  return currentTurnId ? selectTurnTodoPlan(state, currentTurnId) : null;
};

/**
 * Get chat history loading state
 */
export const selectChatHistoryLoading = (state: RootState): boolean => {
  return state.chatSlice?.chatHistoryLoading || false;
};

/**
 * Get chat history error
 */
export const selectChatHistoryError = (state: RootState): string | null => {
  return state.chatSlice?.chatHistoryError || null;
};

/**
 * Get currently selected use case (e.g., 'past-deals', 'offer-letter')
 */
export const selectSelectedUseCase = (state: RootState): string | null => {
  return state.chatSlice?.selectedUseCase || null;
};

export default chatSlice.reducer;
